import React from 'react';
import PropTypes from 'prop-types';
// utils
import { hardReset, isStaleBuildError } from '../utils/hardReset';

// ----------------------------------------------------------------------

// The console had no error boundary anywhere, so a single component throwing
// during render, commit or a layout effect unmounted the whole tree and left a
// white page with nothing to click and nothing in the console but a minified
// identifier. This is the safety net.
//
// It is deliberately written with plain DOM and inline styles: no MUI, no
// theme, no context, no router. A boundary that depends on the app it is
// catching can fail while rendering the failure.

const STORAGE_KEY = 'deskos:last-crash';

// Both buttons purge before they navigate.
//
// They used to be location.reload() and location.assign('/'), which are both
// navigations, and a navigation is exactly what a stale service worker
// intercepts. That is why pressing Reload on a stuck WebView did nothing at all
// and people were reinstalling the app to get out. hardReset() unregisters the
// worker and empties Cache Storage first, so the navigation that follows is
// answered by the server. It leaves localStorage alone, so the user stays
// signed in.
const reloadPage = () => {
  hardReset({ to: window.location.pathname + window.location.search, force: true });
};

const goHome = () => hardReset({ to: '/', force: true });

const wrap = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  boxSizing: 'border-box',
  background: '#F9FAFB',
  color: '#212B36',
  fontFamily: '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const card = {
  width: '100%',
  maxWidth: 560,
  background: '#FFFFFF',
  border: '1px solid #E5E8EB',
  borderRadius: 16,
  padding: '32px 28px',
  boxShadow: '0 12px 32px rgba(145, 158, 171, 0.16)',
};

const buttonBase = {
  appearance: 'none',
  border: 'none',
  borderRadius: 8,
  padding: '10px 18px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: '', showDetail: false, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    const componentStack = errorInfo?.componentStack || '';

    // The stack React prints for a production build is the async origin of the
    // update, not the component that threw. `componentStack` is the part that
    // actually names it, so keep it where a person can read it back later.
    const record = {
      at: new Date().toISOString(),
      url: window.location.href,
      name: error?.name || 'Error',
      message: error?.message || String(error),
      stack: error?.stack || '',
      componentStack,
    };

    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (ignored) {
      // Private mode or a full quota. The console log below is still there.
    }

    // eslint-disable-next-line no-console
    console.error('[DeskOS] uncaught render error\n', record.message, '\n', componentStack, '\n', error);

    if (isStaleBuildError(error)) {
      // The build moved under us. Purge and land on the current one.
      // hardReset() has its own cooldown, so a chunk that is genuinely missing
      // rather than merely stale falls through to the screen below instead of
      // spinning the page.
      hardReset().then((started) => {
        if (!started) this.setState({ componentStack });
      });
      return;
    }

    this.setState({ componentStack });
  }

  handleCopy = () => {
    const { error, componentStack } = this.state;
    const text = [
      `${error?.name || 'Error'}: ${error?.message || String(error)}`,
      window.location.href,
      '',
      error?.stack || '',
      '',
      componentStack,
    ].join('\n');

    const done = () => this.setState({ copied: true });

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {});
      return;
    }
    // Older WebViews, which is where the mobile apps run.
    const field = document.createElement('textarea');
    field.value = text;
    document.body.appendChild(field);
    field.select();
    try {
      document.execCommand('copy');
      done();
    } catch (ignored) {
      // Leave the detail on screen to read instead.
    }
    document.body.removeChild(field);
  };

  render() {
    const { error, componentStack, showDetail, copied } = this.state;
    const { children } = this.props;

    if (!error) return children;

    return (
      <div style={wrap}>
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.6, color: '#8B2FD0', marginBottom: 10 }}>
            DESKOS
          </div>

          <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, lineHeight: 1.4 }}>
            This page stopped responding
          </h1>

          <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.6, color: '#637381' }}>
            Something went wrong while drawing this screen. Nothing you were working on has been sent, so reloading is
            safe. If it happens again, send us the details below.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
            <button
              type="button"
              onClick={reloadPage}
              style={{ ...buttonBase, background: '#8B2FD0', color: '#FFFFFF' }}
            >
              Reload
            </button>
            <button
              type="button"
              onClick={goHome}
              style={{ ...buttonBase, background: '#F4F6F8', color: '#212B36' }}
            >
              Back to start
            </button>
            <button
              type="button"
              onClick={this.handleCopy}
              style={{ ...buttonBase, background: 'transparent', color: '#637381' }}
            >
              {copied ? 'Copied' : 'Copy details'}
            </button>
          </div>

          <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.6, color: '#919eab' }}>
            Still stuck after reloading?{' '}
            <a href="/reset.html" style={{ color: '#8B2FD0', fontWeight: 600 }}>
              Repair the app
            </a>
            . This clears the stored copy without signing you out.
          </p>

          <button
            type="button"
            onClick={() => this.setState({ showDetail: !showDetail })}
            style={{
              ...buttonBase,
              background: 'transparent',
              padding: 0,
              fontSize: 13,
              fontWeight: 600,
              color: '#8B2FD0',
            }}
          >
            {showDetail ? 'Hide technical details' : 'Show technical details'}
          </button>

          {showDetail && (
            <pre
              style={{
                marginTop: 12,
                marginBottom: 0,
                maxHeight: 260,
                overflow: 'auto',
                background: '#F4F6F8',
                border: '1px solid #E5E8EB',
                borderRadius: 8,
                padding: 12,
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '#454F5B',
              }}
            >
              {`${error?.name || 'Error'}: ${error?.message || String(error)}\n\n${error?.stack || ''}\n${componentStack}`}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

AppErrorBoundary.propTypes = {
  children: PropTypes.node,
};

export default AppErrorBoundary;
